import React, { useState } from 'react';

const DAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const SetHours = ({ gymData, onComplete, onBack }) => {
  const [hoursType, setHoursType] = useState('24/7'); // '24/7', 'standard', 'custom'
  const [standardHours, setStandardHours] = useState({
    open: '06:00',
    close: '22:00'
  });
  const [customHours, setCustomHours] = useState(
    DAYS.reduce((acc, day) => ({
      ...acc,
      [day.value]: { open: '06:00', close: '22:00', enabled: true }
    }), {})
  );

  const handleStandardChange = (field, value) => {
    setStandardHours(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomChange = (day, field, value) => {
    setCustomHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const toggleDayEnabled = (day) => {
    setCustomHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled: !prev[day].enabled
      }
    }));
  };

  const handleSkip = () => {
    onComplete({ hours: null });
  };

  const handleContinue = () => {
    let hoursData;

    if (hoursType === '24/7') {
      hoursData = { is24_7: true };
    } else if (hoursType === 'standard') {
      hoursData = {
        is24_7: false,
        schedule: DAYS.map(day => ({
          day: day.value,
          open: standardHours.open,
          close: standardHours.close
        }))
      };
    } else {
      // Only include enabled days in custom hours
      hoursData = {
        is24_7: false,
        schedule: DAYS
          .filter(day => customHours[day.value].enabled)
          .map(day => ({
            day: day.value,
            open: customHours[day.value].open,
            close: customHours[day.value].close
          }))
      };
    }

    onComplete({ hours: hoursData });
  };

  return (
    <div className="bg-white rounded-xl p-6 sm:p-8 border-2 border-slate-900 shadow-retro">
      <div className="flex items-center gap-2 mb-6">
        <span className="w-1 h-8 bg-gradient-to-b from-teal-500 to-purple-500 rounded"></span>
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Set Operating Hours</h2>
      </div>

      <p className="text-slate-700 mb-6">
        When is {gymData.gym.name} open for board reservations? You can always change this later.
      </p>

      <div className="space-y-4 mb-6">
        {/* 24/7 Option */}
        <button
          onClick={() => setHoursType('24/7')}
          className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
            hoursType === '24/7'
              ? 'border-teal-500 bg-teal-50 shadow-retro'
              : 'border-slate-900 hover:border-teal-500 hover:bg-teal-50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-slate-900">24/7 Access</p>
              <p className="text-sm text-slate-600">Members can book anytime</p>
            </div>
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
              hoursType === '24/7' ? 'border-teal-500 bg-teal-500' : 'border-slate-900'
            }`}>
              {hoursType === '24/7' && (
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          </div>
        </button>

        {/* Standard Hours Option */}
        <button
          onClick={() => setHoursType('standard')}
          className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
            hoursType === 'standard'
              ? 'border-teal-500 bg-teal-50 shadow-retro'
              : 'border-slate-900 hover:border-teal-500 hover:bg-teal-50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-slate-900">Standard Hours</p>
              <p className="text-sm text-slate-600">Same hours every day</p>
            </div>
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
              hoursType === 'standard' ? 'border-teal-500 bg-teal-500' : 'border-slate-900'
            }`}>
              {hoursType === 'standard' && (
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          </div>
        </button>

        {hoursType === 'standard' && (
          <div className="pl-4 pr-4 pb-4 pt-2 bg-teal-50 rounded-lg border-2 border-teal-500">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Open Time</label>
                <input
                  type="time"
                  value={standardHours.open}
                  onChange={(e) => handleStandardChange('open', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-slate-900 rounded-lg font-medium"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Close Time</label>
                <input
                  type="time"
                  value={standardHours.close}
                  onChange={(e) => handleStandardChange('close', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-slate-900 rounded-lg font-medium"
                />
              </div>
            </div>
          </div>
        )}

        {/* Custom Hours Option */}
        <button
          onClick={() => setHoursType('custom')}
          className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
            hoursType === 'custom'
              ? 'border-teal-500 bg-teal-50 shadow-retro'
              : 'border-slate-900 hover:border-teal-500 hover:bg-teal-50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-slate-900">Custom Hours</p>
              <p className="text-sm text-slate-600">Different hours each day</p>
            </div>
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
              hoursType === 'custom' ? 'border-teal-500 bg-teal-500' : 'border-slate-900'
            }`}>
              {hoursType === 'custom' && (
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          </div>
        </button>

        {hoursType === 'custom' && (
          <div className="pl-4 pr-4 pb-4 pt-2 bg-teal-50 rounded-lg border-2 border-teal-500 max-h-96 overflow-y-auto">
            <p className="text-sm text-slate-600 mb-3 font-semibold">
              Uncheck days when your gym is closed:
            </p>
            <div className="space-y-3">
              {DAYS.map(day => {
                const isEnabled = customHours[day.value].enabled;
                return (
                  <div key={day.value}>
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        id={`day-${day.value}`}
                        checked={isEnabled}
                        onChange={() => toggleDayEnabled(day.value)}
                        className="h-4 w-4 cursor-pointer"
                      />
                      <label
                        htmlFor={`day-${day.value}`}
                        className={`text-sm font-semibold cursor-pointer ${
                          isEnabled ? 'text-slate-700' : 'text-slate-400 line-through'
                        }`}
                      >
                        {day.label}
                      </label>
                    </div>
                    {isEnabled && (
                      <div className="grid grid-cols-2 gap-2 ml-6">
                        <input
                          type="time"
                          value={customHours[day.value].open}
                          onChange={(e) => handleCustomChange(day.value, 'open', e.target.value)}
                          className="px-3 py-2 border-2 border-slate-900 rounded-lg font-medium text-sm"
                        />
                        <input
                          type="time"
                          value={customHours[day.value].close}
                          onChange={(e) => handleCustomChange(day.value, 'close', e.target.value)}
                          className="px-3 py-2 border-2 border-slate-900 rounded-lg font-medium text-sm"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation"
        >
          ← Back
        </button>
        <button
          onClick={handleSkip}
          className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation"
        >
          Skip for Now
        </button>
        <button
          onClick={handleContinue}
          className="flex-1 px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation"
        >
          Continue →
        </button>
      </div>
    </div>
  );
};

export default SetHours;
